import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "../AxiosInstance";
import OverlayWeekPicker from "./OverlayWeekPicker";
import {
    getEarliestEditableForecastWeekStart,
    getEditableForecastWeekStarts,
    getTodayInUkCalendar,
    isForecastWeekInPast
} from "../Utilities/forecastWeekEditability";

const buildDateMap = (weekStartDateOptions) => {
    return weekStartDateOptions.reduce((acc, dateOption) => {
        const [day, month, year] = dateOption.split("-").map(Number);
        acc[dateOption] = `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
        return acc;
    }, {});
};

const extractValidationMessages = (errors) => {
    if (!errors || typeof errors !== "object") {
        return [];
    }

    return Object.values(errors)
        .flatMap((entry) => {
            if (Array.isArray(entry)) {
                return entry.map((value) => String(value));
            }
            if (typeof entry === "string") {
                return [entry];
            }
            return [];
        })
        .filter(Boolean);
};

const getApiErrorMessage = (error, fallbackMessage) => {
    const data = error?.response?.data;
    if (!data) {
        return fallbackMessage;
    }

    if (typeof data === "string") {
        return data;
    }

    if (Array.isArray(data)) {
        return data.map((item) => String(item)).join(" ");
    }

    if (typeof data === "object") {
        const validationMessages = extractValidationMessages(data.errors);
        if (validationMessages.length > 0) {
            return validationMessages.join(" ");
        }

        if (typeof data.title === "string" && data.title.trim() !== "") {
            return data.title;
        }
    }

    return fallbackMessage;
};

const parseDateOption = (value) => {
    if (!value || typeof value !== "string") {
        return null;
    }

    const [day, month, year] = value.split("-").map(Number);
    if (!day || !month || !year) {
        return null;
    }

    const parsed = new Date(year, month - 1, day);
    parsed.setHours(0, 0, 0, 0);
    return parsed;
};

const OverlayPeriodsTable = ({ forecastId, forecastPeriodId, allowPastWeeks = false }) => {
    const [overlayPeriods, setOverlayPeriods] = useState([]);
    const [weekStartDateOptions, setWeekStartDateOptions] = useState([]);
    const [canApplyFullYear, setCanApplyFullYear] = useState(true);
    const [isOverlayPeriodsLoaded, setIsOverlayPeriodsLoaded] = useState(false);
    const [selectedOverlayPeriodId, setSelectedOverlayPeriodId] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [percentage, setPercentage] = useState("");
    const [formError, setFormError] = useState("");
    const [applyPercentage, setApplyPercentage] = useState("");
    const [applyError, setApplyError] = useState("");
    const [snackbar, setSnackbar] = useState(null);

    const dateMap = useMemo(() => buildDateMap(weekStartDateOptions), [weekStartDateOptions]);
    const todayLocal = useMemo(() => getTodayInUkCalendar(), []);
    const firstWeekStartDate = useMemo(() => parseDateOption(weekStartDateOptions[0]), [weekStartDateOptions]);
    const earliestEditableWeekStartOption = useMemo(
        () => getEarliestEditableForecastWeekStart(weekStartDateOptions, allowPastWeeks, todayLocal),
        [weekStartDateOptions, allowPastWeeks, todayLocal]
    );
    const isForecastStarted = useMemo(
        () => Boolean(firstWeekStartDate && firstWeekStartDate < todayLocal),
        [firstWeekStartDate, todayLocal]
    );
    const applyButtonLabel = allowPastWeeks || !isForecastStarted ? "Apply to Full Year" : "Apply to Rest of Year";
    const canShowApplyButton = isOverlayPeriodsLoaded && canApplyFullYear;
    const createStartDateOptions = useMemo(() => {
        return getEditableForecastWeekStarts(weekStartDateOptions, allowPastWeeks, todayLocal);
    }, [allowPastWeeks, weekStartDateOptions, todayLocal]);
    const selectedOverlayPeriod = useMemo(
        () => overlayPeriods.find((item) => item.overlayPeriodId === selectedOverlayPeriodId) || null,
        [overlayPeriods, selectedOverlayPeriodId]
    );
    const selectedOverlayStartDate = useMemo(
        () => parseDateOption(selectedOverlayPeriod?.startDate),
        [selectedOverlayPeriod]
    );
    const selectedOverlayHasStartedOrIsCurrentWeek = useMemo(() => {
        if (!selectedOverlayStartDate) {
            return false;
        }
        return selectedOverlayStartDate <= todayLocal;
    }, [selectedOverlayStartDate, todayLocal]);
    const editStartDateOptions = useMemo(() => {
        if (!isEditMode) {
            return weekStartDateOptions;
        }

        if (selectedOverlayHasStartedOrIsCurrentWeek) {
            return startDate ? [startDate] : [];
        }

        return getEditableForecastWeekStarts(weekStartDateOptions, allowPastWeeks, todayLocal);
    }, [isEditMode, selectedOverlayHasStartedOrIsCurrentWeek, startDate, weekStartDateOptions, allowPastWeeks, todayLocal]);
    const effectiveStartDateOptions = isEditMode ? editStartDateOptions : createStartDateOptions;
    const isStartWeekLocked = isEditMode && selectedOverlayHasStartedOrIsCurrentWeek;
    const canDeleteSelectedOverlay = useMemo(() => {
        if (!selectedOverlayPeriod || !selectedOverlayStartDate) {
            return false;
        }
        return !isForecastWeekInPast(selectedOverlayPeriod.startDate, allowPastWeeks, todayLocal);
    }, [selectedOverlayPeriod, selectedOverlayStartDate, allowPastWeeks, todayLocal]);
    const endWeekStartDateOptions = useMemo(() => {
        const baseOptions = weekStartDateOptions.filter((option) => {
            if (!(isEditMode && selectedOverlayHasStartedOrIsCurrentWeek)) {
                return true;
            }

            return !isForecastWeekInPast(option, allowPastWeeks, todayLocal);
        });

        if (!startDate || endDate) {
            return baseOptions;
        }

        const startIndex = baseOptions.indexOf(startDate);
        if (startIndex < 0) {
            return baseOptions;
        }

        return baseOptions.slice(startIndex);
    }, [weekStartDateOptions, startDate, endDate, isEditMode, selectedOverlayHasStartedOrIsCurrentWeek, allowPastWeeks, todayLocal]);

    const loadOverlayPeriods = async () => {
        setIsOverlayPeriodsLoaded(false);
        try {
            const response = await axiosInstance.get(`/forecasts/${forecastId}/overlay-periods?forecastPeriodId=${forecastPeriodId}`);
            setOverlayPeriods(response.data.overlayPeriods || []);
            setWeekStartDateOptions(response.data.weekStartDateOptions || []);
            setCanApplyFullYear(response.data.canApplyFullYear === true);
            setIsOverlayPeriodsLoaded(true);
            setSelectedOverlayPeriodId(null);
        } catch (error) {
            setSnackbar({
                severity: "error",
                message: getApiErrorMessage(error, "Failed to load overlay periods.")
            });
        }
    };

    useEffect(() => {
        if (!forecastId || !forecastPeriodId) {
            return;
        }

        loadOverlayPeriods();
    }, [forecastId, forecastPeriodId]);

    const openCreateDialog = () => {
        setIsEditMode(false);
        setStartDate("");
        setEndDate("");
        setPercentage("");
        setFormError("");
        setIsDialogOpen(true);
    };

    const openEditDialog = () => {
        if (!selectedOverlayPeriod) {
            return;
        }

        setIsEditMode(true);
        setStartDate(selectedOverlayPeriod.startDate);
        setEndDate(selectedOverlayPeriod.endDate);
        setPercentage(selectedOverlayPeriod.percentage.toString());
        setFormError("");
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        if (isSubmitting) {
            return;
        }

        setIsDialogOpen(false);
        setFormError("");
    };

    const validatePercentage = (value) => {
        if (value === "") {
            return "Percentage is required.";
        }

        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
            return "Percentage must be a valid number.";
        }

        const decimalPart = value.includes(".") ? value.split(".")[1] : "";
        if (decimalPart.length > 4) {
            return "Percentage must have no more than 4 decimal places.";
        }

        if (numericValue < 0) {
            return "Percentage must be greater than or equal to 0.";
        }

        return null;
    };

    const handleSubmit = async () => {
        const percentageValidationError = validatePercentage(percentage);
        if (percentageValidationError) {
            setFormError(percentageValidationError);
            return;
        }

        if (!startDate || !endDate) {
            setFormError("Start and End dates are required.");
            return;
        }

        const earliestEditableWeekStart = earliestEditableWeekStartOption
            ? parseDateOption(earliestEditableWeekStartOption)
            : null;
        const selectedStartDate = parseDateOption(startDate);
        const selectedEndDate = parseDateOption(endDate);

        if (!earliestEditableWeekStart) {
            setFormError("No current or future weeks are available for this forecast.");
            return;
        }

        if (isEditMode && !allowPastWeeks && selectedOverlayHasStartedOrIsCurrentWeek) {
            if (selectedOverlayPeriod && startDate !== selectedOverlayPeriod.startDate) {
                setFormError("Start Week cannot be changed for an overlay that has already started.");
                return;
            }

            if (!selectedEndDate || selectedEndDate < earliestEditableWeekStart) {
                setFormError("End Week must be in the current or a future forecast week.");
                return;
            }
        } else if (!allowPastWeeks &&
            (!selectedStartDate || selectedStartDate < earliestEditableWeekStart)) {
            setFormError("Start Week must be in the current or a future forecast week.");
            return;
        }

        const startDateIso = dateMap[startDate];
        const endDateIso = dateMap[endDate];
        if (!startDateIso || !endDateIso) {
            setFormError("Start and End weeks are required.");
            return;
        }

        if (endDateIso < startDateIso) {
            setFormError("End Week must be on or after Start Week.");
            return;
        }

        const payload = {
            forecastPeriodId,
            startDate: startDateIso,
            endDate: endDateIso,
            percentage: Number(percentage)
        };

        setIsSubmitting(true);
        setFormError("");
        try {
            if (isEditMode && selectedOverlayPeriod) {
                await axiosInstance.put(`/forecasts/${forecastId}/overlay-periods/${selectedOverlayPeriod.overlayPeriodId}`, payload);
            } else {
                await axiosInstance.post(`/forecasts/${forecastId}/overlay-periods`, payload);
            }

            setIsDialogOpen(false);
            await loadOverlayPeriods();
            setSnackbar({
                severity: "success",
                message: isEditMode ? "Overlay period updated." : "Overlay period created."
            });
        } catch (error) {
            const message = getApiErrorMessage(error, "Failed to save overlay period.");
            setFormError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedOverlayPeriod) {
            return;
        }

        setIsSubmitting(true);
        try {
            await axiosInstance.delete(`/forecasts/${forecastId}/overlay-periods/${selectedOverlayPeriod.overlayPeriodId}?forecastPeriodId=${forecastPeriodId}`);
            setIsDeleteDialogOpen(false);
            await loadOverlayPeriods();
            setSnackbar({
                severity: "success",
                message: "Overlay period deleted."
            });
        } catch (error) {
            setSnackbar({
                severity: "error",
                message: getApiErrorMessage(error, "Failed to delete overlay period.")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleApplyFullYear = async () => {
        const percentageValidationError = validatePercentage(applyPercentage);
        if (percentageValidationError) {
            setApplyError(percentageValidationError);
            return;
        }

        if (weekStartDateOptions.length === 0) {
            setApplyError("No week start dates are available for this forecast.");
            return;
        }

        if (!earliestEditableWeekStartOption) {
            setApplyError("No current or future weeks are available for this forecast.");
            return;
        }

        const applyStartDateOption = earliestEditableWeekStartOption;

        setIsSubmitting(true);
        setApplyError("");
        try {
            await axiosInstance.post(`/forecasts/${forecastId}/overlay-periods`, {
                forecastPeriodId,
                startDate: dateMap[applyStartDateOption],
                endDate: dateMap[weekStartDateOptions[weekStartDateOptions.length - 1]],
                percentage: Number(applyPercentage)
            });
            setIsApplyDialogOpen(false);
            setApplyPercentage("");
            await loadOverlayPeriods();
            setSnackbar({
                severity: "success",
                message: allowPastWeeks || !isForecastStarted ? "Overlay applied to full year." : "Overlay applied to rest of year."
            });
        } catch (error) {
            setApplyError(getApiErrorMessage(error, "Failed to apply full-year overlay."));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ mt: 2, mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="h6">Overlay</Typography>
                <Box>
                    {canShowApplyButton && (
                        <Button
                            variant="outlined"
                            size="small"
                            sx={{ mr: 1 }}
                            onClick={() => {
                                setApplyError("");
                                setApplyPercentage("");
                                setIsApplyDialogOpen(true);
                            }}
                        >
                            {applyButtonLabel}
                        </Button>
                    )}
                    <IconButton aria-label="createOverlayPeriod" onClick={openCreateDialog}>
                        <AddIcon />
                    </IconButton>
                    <IconButton aria-label="editOverlayPeriod" onClick={openEditDialog} disabled={!selectedOverlayPeriod}>
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        aria-label="deleteOverlayPeriod"
                        onClick={() => setIsDeleteDialogOpen(true)}
                        disabled={!canDeleteSelectedOverlay}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Start Week</TableCell>
                            <TableCell>End Week</TableCell>
                            <TableCell align="right">Overlay %</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {overlayPeriods.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3}>No overlay periods defined.</TableCell>
                            </TableRow>
                        )}
                        {overlayPeriods.map((row) => {
                            const isSelected = selectedOverlayPeriodId === row.overlayPeriodId;
                            return (
                                <TableRow
                                    key={row.overlayPeriodId}
                                    hover
                                    selected={isSelected}
                                    onClick={() => setSelectedOverlayPeriodId(row.overlayPeriodId)}
                                    sx={{ cursor: "pointer" }}
                                >
                                    <TableCell>{row.startDate}</TableCell>
                                    <TableCell>{row.endDate}</TableCell>
                                    <TableCell align="right">{Number(row.percentage).toFixed(4)}%</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={isDialogOpen} onClose={closeDialog}>
                <DialogTitle>{isEditMode ? "Edit Overlay Period" : "Create Overlay Period"}</DialogTitle>
                <DialogContent sx={{ minWidth: 420 }}>
                    <OverlayWeekPicker
                        label="Start Week"
                        value={startDate}
                        onChange={setStartDate}
                        weekStartDateOptions={effectiveStartDateOptions}
                        weekNumberBaseDateOption={weekStartDateOptions[0]}
                        disabled={isStartWeekLocked}
                    />

                    <OverlayWeekPicker
                        label="End Week"
                        value={endDate}
                        onChange={setEndDate}
                        weekStartDateOptions={endWeekStartDateOptions}
                        weekNumberBaseDateOption={weekStartDateOptions[0]}
                    />

                    <TextField
                        margin="dense"
                        label="Overlay %"
                        fullWidth
                        autoComplete="off"
                        type="number"
                        inputProps={{ step: 0.0001, min: 0 }}
                        value={percentage}
                        onChange={(event) => setPercentage(event.target.value)}
                    />

                    {formError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {formError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={isSubmitting}>
                        {isEditMode ? "Save" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                <DialogTitle>Delete Overlay Period</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this overlay period?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)} disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleDelete} disabled={isSubmitting}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isApplyDialogOpen} onClose={() => setIsApplyDialogOpen(false)}>
                <DialogTitle>{`Apply Overlay to ${allowPastWeeks || !isForecastStarted ? "Full Year" : "Rest of Year"}`}</DialogTitle>
                <DialogContent sx={{ minWidth: 420 }}>
                    <TextField
                        margin="dense"
                        label="Overlay %"
                        fullWidth
                        autoComplete="off"
                        type="number"
                        inputProps={{ step: 0.0001, min: 0 }}
                        value={applyPercentage}
                        onChange={(event) => setApplyPercentage(event.target.value)}
                    />
                    {applyError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {applyError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsApplyDialogOpen(false)} disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleApplyFullYear} disabled={isSubmitting}>Apply</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snackbar}
                autoHideDuration={5000}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {snackbar ? <Alert severity={snackbar.severity}>{snackbar.message}</Alert> : <span />}
            </Snackbar>
        </Box>
    );
};

export default OverlayPeriodsTable;
